import { useEffect, useMemo, useRef, useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierCustomerConnection,
  type CarrierCustomerTier,
  usePatchCarrierCustomer,
} from "@/api/carrier";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { TablePager } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CustomerBlockButton, CustomerDetailDrawer } from "./customer-detail";
import { customerViewModel } from "./customer-view-model";
import { isForbidden, mutationFailure } from "./mutation-guards";

export interface CustomersWorkspaceProps {
  customers: CarrierCustomerConnection[];
  tiers: CarrierCustomerTier[];
  pagination: { total: number; limit: number; offset: number } | null;
  isLoading: boolean;
  error: unknown;
  isOwner: boolean;
  search: string;
  status: "ACTIVE" | "BLOCKED" | "REMOVED" | "ALL";
  onSearchChange: (value: string) => void;
  onStatusChange: (value: "ACTIVE" | "BLOCKED" | "REMOVED" | "ALL") => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRetry: () => void;
}

export function CustomersWorkspace({
  customers,
  tiers,
  pagination,
  isLoading,
  error,
  isOwner,
  search,
  status,
  onSearchChange,
  onStatusChange,
  onPageChange,
  onPageSizeChange,
  onRetry,
}: CustomersWorkspaceProps) {
  const patchCustomer = usePatchCarrierCustomer();
  const assignmentLock = useRef<string | null>(null);
  const [assignmentPending, setAssignmentPending] = useState<string | null>(
    null
  );
  const [assignmentValues, setAssignmentValues] = useState<
    Record<string, string>
  >({});
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [detail, setDetail] = useState<CarrierCustomerConnection | null>(null);
  const forbidden = isForbidden(error);
  const showRows = customers.length > 0 && !forbidden;
  const customerItems = useMemo(
    () =>
      customers.map((row) => ({
        row,
        view: customerViewModel(row, tiers),
      })),
    [customers, tiers]
  );

  useEffect(() => {
    setAssignmentValues((current) => {
      let next = current;
      for (const row of customers) {
        const selectedTier = current[row.customerId];
        if (selectedTier === undefined || selectedTier !== (row.tierId ?? "")) {
          continue;
        }
        if (next === current) next = { ...current };
        delete next[row.customerId];
      }
      return next;
    });
  }, [customers]);

  const assignTier = async (row: CarrierCustomerConnection, tierId: string) => {
    if (assignmentLock.current !== null) return;
    assignmentLock.current = row.customerId;
    setAssignmentValues((current) => ({
      ...current,
      [row.customerId]: tierId,
    }));
    setAssignmentPending(row.customerId);
    setAssignmentError(null);
    try {
      await patchCustomer.mutateAsync({
        customerId: row.customerId,
        tierId: tierId || null,
      });
      toast.success("Customer tier updated.");
    } catch (mutationError) {
      const message = mutationFailure(
        mutationError,
        "update this customer's tier"
      );
      setAssignmentError(message);
      toast.error(message);
    } finally {
      assignmentLock.current = null;
      setAssignmentPending(null);
    }
  };

  return (
    <div className="carrier-page">
      <PageHeader
        title="Customers"
        actions={
          !isOwner ? (
            <span className="rounded-full border px-3 py-1 text-xs font-semibold text-muted-foreground">
              Read-only dispatcher view
            </span>
          ) : undefined
        }
      />

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-2 border-b p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
          <Input
            type="search"
            aria-label="Search customers"
            className="w-full sm:w-64"
            placeholder="Search customers..."
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
          />
          <Select
            value={status}
            onValueChange={(value) => onStatusChange(value as typeof status)}
          >
            <SelectTrigger
              aria-label="Filter customers by status"
              size="sm"
              className="w-full sm:w-44"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="BLOCKED">Blocked</SelectItem>
              <SelectItem value="REMOVED">Left</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {assignmentError && (
          <p
            role="alert"
            className="border-b px-4 py-2 text-xs text-destructive"
          >
            {assignmentError} Your current selection was preserved.
          </p>
        )}

        {error != null && (
          <div
            role="alert"
            className="flex flex-wrap items-center gap-3 border-b p-4 text-sm"
          >
            <span className="text-destructive">
              {forbidden
                ? "You do not have permission to view customers."
                : customers.length > 0
                  ? "Customers could not refresh. Showing the most recent data."
                  : "Failed to load customers."}
            </span>
            {!forbidden && (
              <Button variant="outline" size="sm-touch" onClick={onRetry}>
                Retry
              </Button>
            )}
          </div>
        )}

        {isLoading && customers.length === 0 && !error && (
          <p
            role="status"
            className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground"
          >
            <Loader2 className="h-4 w-4 animate-spin" /> Loading customers...
          </p>
        )}

        {!isLoading && !error && customers.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {search.trim() || status !== "ALL"
              ? "No customers match your filters."
              : "No customers yet."}
          </p>
        )}

        {showRows && (
          <>
            <ul aria-label="Customer summaries" className="divide-y lg:hidden">
              {customerItems.map(({ row, view }) => (
                <li key={row.id}>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto min-h-11 w-full items-start justify-start gap-3 rounded-none p-3 text-left whitespace-normal hover:bg-muted/50"
                    aria-label={`View customer ${view.name}`}
                    onClick={() => setDetail(row)}
                  >
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            {view.name}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {view.email}
                          </p>
                        </div>
                        <StatusBadge status={view.status} />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {view.tierName} tier · {row.orderCount} order
                        {row.orderCount === 1 ? "" : "s"}
                      </p>
                    </div>
                    <Eye className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                  </Button>
                </li>
              ))}
            </ul>

            <div className="hidden lg:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead className="w-px whitespace-nowrap">
                      Status
                    </TableHead>
                    <TableHead className="w-44">Tier</TableHead>
                    <TableHead className="w-px whitespace-nowrap">
                      Orders
                    </TableHead>
                    <TableHead className="w-px whitespace-nowrap text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customerItems.map(({ row, view }) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <span className="block max-w-56 truncate font-medium">
                          {view.name}
                        </span>
                        {row.customer.email && (
                          <span className="block max-w-56 truncate text-xs text-muted-foreground">
                            {view.email}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <StatusBadge status={view.status} />
                      </TableCell>
                      <TableCell>
                        {/* Width lives on this outer div, not the select:
                            NativeSelect anchors its chevron to its own
                            wrapper, so a fixed-width select inside a
                            full-width cell drifts the chevron and clips at
                            the table edge. max-w-full lets it compress in
                            narrow cells instead of overflowing. */}
                        <div className="w-40 max-w-full">
                          <NativeSelect
                            aria-label={`Tier for ${view.name}`}
                            density="compact"
                            className="text-xs"
                            value={
                              assignmentValues[row.customerId] ??
                              row.tierId ??
                              ""
                            }
                            disabled={
                              !isOwner ||
                              row.removed ||
                              assignmentPending !== null
                            }
                            onChange={(event) =>
                              void assignTier(row, event.target.value)
                            }
                          >
                            <option value="">Default</option>
                            {tiers.map((tier) => (
                              <option key={tier.id} value={tier.id}>
                                {tier.name}
                              </option>
                            ))}
                          </NativeSelect>
                        </div>
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {row.orderCount}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setDetail(row)}
                          >
                            View
                          </Button>
                          {isOwner && !row.removed && (
                            <CustomerBlockButton row={row} />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {pagination && !forbidden && (
          <div className="border-t p-3">
            <TablePager
              page={Math.floor(pagination.offset / pagination.limit) + 1}
              pageSize={pagination.limit}
              total={pagination.total}
              totalPages={Math.ceil(pagination.total / pagination.limit)}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              disabled={isLoading || assignmentPending !== null}
            />
          </div>
        )}
      </Card>

      {detail && (
        <CustomerDetailDrawer
          open={true}
          onOpenChange={(open) => {
            if (!open && assignmentPending === null) setDetail(null);
          }}
          row={detail}
          tiers={tiers}
          isOwner={isOwner}
          mode="customers"
          tierAssignmentPending={assignmentPending === detail.customerId}
          tierValue={assignmentValues[detail.customerId] ?? detail.tierId ?? ""}
          onAssignTier={(tierId) => void assignTier(detail, tierId)}
        />
      )}
    </div>
  );
}
